import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Ripple } from "primereact/ripple";
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from "primereact/button";
import { InputNumber, InputNumberChangeEvent } from 'primereact/inputnumber';
import { Dialog } from 'primereact/dialog';
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { goToTop } from "../../../../../../../Components/goToTop";
import DownloadSOW from "../../DownloadSOW";
import { getAppCurrencyType } from "../../../../../../../Utils/Common";
import { Section, sections, SubSection, SectionItem, SubSectionItem, SubSectionItemAmount, Preliminaries, preliminaries } from "./SampleCustomSOW";
import { useAppDispatch } from "../../../../../../../Controllers/hooks";
import { getSowSectionAndPriliminary } from "../../../../../../../Controllers/Thunks/tenderThunks";
import { ProjectTenderDetailInterface } from "../../../../../../../Controllers/Types/Tender/projectTenderDetailsInterface";
import DownloadSOWTenderList from "../../DownloadSOWTenderList";

const ViewSOW: React.FC = () => {
    const navigate = useNavigate();
    const toast = useRef<Toast>(null);
    const dispatch = useAppDispatch();
    const currencySymbol = getAppCurrencyType();

    const location = useLocation();
    const tenderPart = location.state as ProjectTenderDetailInterface

    const [loading, setLoading] = useState<boolean>(false);
    const [showSideBar, setShowSideBar] = useState<boolean>(false);
    const [showDownloadModal, setShowDownloadModal] = useState<boolean>(false);

    /* Cover page */
    const [showCoverPage, setShowCoverpage] = useState<boolean>(true);
    const [coverPageBody, setCoverPageBody] = useState<string>('Erection of four storey building comprising provision of flexible A1 (retail) /A2 (financial and professional services) on the ground floor with 6 one bedroom and 3 two-bedroom flats on upper floors.');

    /* Sections */
    const [sectionData, setSectionData] = useState<Section[]>([]);
    const [showSection, setShowSection] = useState<boolean>(false);
    const [hasSection, setHasSection] = useState<boolean>(false);

    /* Sub section */
    const [showSubSection, setShowSubSection] = useState<boolean>(false);
    const [currentSection, setCurrentSection] = useState<Section | null>(null);

    // for sub section
    const [hasSubSection, setHasSubSection] = useState<boolean>(false);

    // for item
    const [hasSectionitem, setHasSectionItem] = useState<boolean>(false);

    // for sub section item
    const [currentSubSection, setCurrentSubSection] = useState<SubSection | null>(null);
    const [showSubSectionItem, setShowSubSectionItem] = useState<boolean>(false);
    const [hasSubSectionItem, setHasSubSectionItem] = useState<boolean>(false);

    /* Preliminaries */
    const [showPreliminaries, setShowPreliminaries] = useState<boolean>(false);
    const [preliminariesData, setPreliminariesData] = useState<Preliminaries[]>([]);
    const [preliminary, setPreliminary] = useState<boolean>(false);

    /* For search */
    const [showSearchModal, setShowSearchModal] = useState<boolean>(false);
    const [searchKey, setSearchKey] = useState<string>('');
    const [filteredSectionItemData, setFilteredSectionItemData] = useState<SectionItem[]>([]);
    const [filteredPreliminariesItemData, setFilteredPreliminariesItemData] = useState<Preliminaries[]>([]);

    useEffect(() => {
        dispatch(getSowSectionAndPriliminary(tenderPart.project_tender_part_id));
    }, []);

    useEffect(() => {
        const checkSectionHasData = () => {
            if (sectionData.length > 0) {
                setHasSection(true);
            } else {
                setHasSection(false);
            }
        };

        checkSectionHasData();
    }, [sectionData]);

    useEffect(() => {
        const checkPreliminaryHasData = () => {
            if (preliminariesData.length > 0) {
                setPreliminary(true);
            } else {
                setPreliminary(false);
            }
        };

        checkPreliminaryHasData();
    }, [preliminariesData]);


    useEffect(() => {
        if (hasSectionitem && currentSection?.section_item_data) {
            const filtered = currentSection.section_item_data.filter((item) => {
                const searchLower = searchKey.toLowerCase();
                return (
                    item.item_name.toLowerCase().includes(searchLower) ||
                    item.item_no.toLowerCase().includes(searchLower)
                );
            });
            setFilteredSectionItemData(filtered);
        }
    }, [searchKey, hasSectionitem, currentSection?.section_item_data]);


    useEffect(() => {
        if (preliminariesData) {
            const filtered = preliminariesData.filter((item) => {
                const searchLower = searchKey.toLowerCase();
                return (
                    item.ref_number.toLowerCase().includes(searchLower) ||
                    item.item_name.toLowerCase().includes(searchLower)
                );
            });
            setFilteredPreliminariesItemData(filtered);
        }
    }, [searchKey, preliminariesData]);


    const handleShowDownload = () => {
        setShowDownloadModal(true);
    }

    const handleHideDownload = () => {
        setShowDownloadModal(false);
    }


    /* **************************** Common functions ***************************** */
    const scrollToSection = (id: string) => {
        const section = document.getElementById(id);
        if (section) {
            const yOffset = -150;
            const yPosition = section.getBoundingClientRect().top + window.pageYOffset + yOffset;

            window.scrollTo({ top: yPosition, behavior: 'smooth' });
        }
    };

    const formatPrice = (value: number) => {
        return new Intl.NumberFormat('en-UK', {
            style: 'decimal',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);
    };

    const goBack = () => {
        navigate(-1);
        goToTop();
    };

    const handleShowSideBar = () => {
        setShowSideBar(!showSideBar);
    }
    /* *************************************************************************** */


    /* ******************************** Cover page ******************************** */
    const handleShowCoverPage = () => {
        setShowCoverpage(true);
        scrollToSection('coverPage')

        setShowSection(false);
        setShowSubSection(false);
        setShowPreliminaries(false);
        setShowSubSectionItem(false);
    }
    /* **************************************************************************** */


    /* ******************************** Sections ******************************** */
    const handleShowSections = () => {
        setShowSection(true);
        scrollToSection('content');

        setShowCoverpage(false);
        setShowSubSection(false);
        setShowPreliminaries(false);
        setShowSubSectionItem(false);
    }

    const getRowClassName = () => {
        return 'project_table_row';
    };

    /* **************************************************************************** */


    /* ********************************** Sub Section ********************************* */
    const handleShowSubSection = async (section: Section) => {
        setCurrentSection(section);
        scrollToSection(`section${section?.section_no}`)

        if (section?.sub_section_data?.length !== 0) {
            setHasSubSection(true);
        } else {
            setHasSubSection(false);
        }

        if (section?.section_item_data?.length !== 0) {
            setHasSectionItem(true);
        } else {
            setHasSectionItem(false);
        }

        setShowSubSection(true);

        setShowSection(false);
        setShowCoverpage(false);
        setShowPreliminaries(false);
        setShowSubSectionItem(false);

        setSearchKey('');
    }

    const onCellEditComplete = (e: any) => {
        let { rowData, newValue, field, originalEvent: event } = e;

        if (typeof newValue === 'string' && newValue.trim().length > 0) {
            rowData[field] = parseFloat(newValue.replace(/,/g, ''));
        } else if (typeof newValue === 'number') {
            rowData[field] = newValue;
        } else {
            event.preventDefault();
        }
    };

    const itemUnitNumberEditor = (options: any) => {
        return (
            <InputNumber
                value={options.value}
                onChange={(e: InputNumberChangeEvent) => options.editorCallback(e.value)}
                onKeyDown={(e) => e.stopPropagation()}
                mode="decimal"
                className="editable_number_input"
                useGrouping={false}
                inputClassName="text-center"
            />
        );
    };


    // For sub section item
    const handleShowSubSectionItem = async (subSection: SubSection) => {
        setCurrentSubSection(subSection)
        scrollToSection(`subsection${subSection?.sub_section_no}`)

        if (subSection?.sub_section_item_data?.length !== 0) {
            setHasSubSectionItem(true);
        } else {
            setHasSubSectionItem(false);
        }

        setShowSubSectionItem(true);

        setShowSection(false);
        setShowCoverpage(false);
        setShowPreliminaries(false);
        setShowSubSection(false);

        setSearchKey('');
    }
    /* **************************************************************************** */


    /* ******************************** Preliminaries ******************************** */
    const handleShowPreliminaries = () => {
        setShowPreliminaries(true);
        scrollToSection('preliminary')

        setShowSection(false);
        setShowCoverpage(false);
        setShowSubSection(false);
        setShowSubSectionItem(false);

        setSearchKey('');
    }

    const preliminaryItemNumberEditor = (options: any) => {
        return (
            <InputNumber
                value={options.value}
                onChange={(e: InputNumberChangeEvent) => options.editorCallback(e.value)}
                onKeyDown={(e) => e.stopPropagation()}
                mode="decimal"
                className="editable_number_input"
                useGrouping={false}
                inputClassName="text-center"
            />
        );
    };

    const preliminaryUnitPriceEditor = (options: any) => {
        return (
            <InputNumber
                value={options.value}
                onChange={(e: InputNumberChangeEvent) => options.editorCallback(e.value)}
                onKeyDown={(e) => e.stopPropagation()}
                mode="decimal"
                className="editable_number_input"
                useGrouping={false}
                inputClassName="text-center"
            />
        );
    };
    /* **************************************************************************** */


    /* ********************************** Search ********************************** */
    const handleShowSearchModal = () => {
        setShowSearchModal(true);
    }
    /* **************************************************************************** */


    return (
        <div>
            <div className="project_toolbar">
                <Button icon={`bi ${showSideBar ? 'bi-arrow-left' : 'bi-list'}`} onClick={handleShowSideBar} className="sidebar_toggle_btn" />
                <div className="project_tool_options mobile_wrap">
                    <div className="project_tool_options_sub">
                        <button className={`is_btn project_tool_btn p-ripple`}
                            onClick={() => navigate('/project/detail/default-service/pre-commencement/tender/sow/customize')}>
                            Add
                            <Ripple />
                        </button>
                    </div>

                    <div className="project_tool_options_sub">
                        {((showPreliminaries === true && preliminary === true) || (showSubSection === true && hasSectionitem === true) || (showSubSectionItem === true && hasSubSectionItem === true)) && (
                            <button className={`is_btn project_tool_btn p-ripple`}
                                onClick={handleShowSearchModal}>
                                Search
                                <Ripple />
                            </button>
                        )}
                        <button className={`is_btn project_tool_btn p-ripple`}
                            onClick={handleShowDownload}>
                            Download
                            <Ripple />
                        </button>
                        <button className={`is_btn project_tool_btn p-ripple`}
                            onClick={goBack}>
                            Back
                            <Ripple />
                        </button>
                    </div>
                </div>
            </div>

            <div className={`project_sidebar ${showSideBar ? 'show' : ''}`}>
                <ul className="section_list">
                    {/* Cover page */}
                    <li className="section_list_item">
                        <div className={`section_link_grp ${showCoverPage && 'active'}`}>
                            <i className={`bi bi-caret-right-fill section_link_icon ${(coverPageBody === '') && 'disabled'}`}></i>
                            <button className="section_link is_btn"
                                disabled={coverPageBody === ''}
                                onClick={handleShowCoverPage}>
                                <span>Cover page</span>
                            </button>
                        </div>
                    </li>

                    {/* Sections */}
                    <li className="section_list_item">
                        <div className={`section_link_grp ${showSection && 'active'} ${sections?.length > 0 && 'has_child'} `}>
                            <i className={`bi bi-caret-right-fill section_link_icon ${(sections?.length === 0) && 'disabled'}`}></i>
                            <button className="section_link is_btn"
                                disabled={sections?.length === 0}
                                onClick={handleShowSections}>
                                <span>Contents</span>
                            </button>
                        </div>

                        {sections?.length > 0 && (
                            <ul className="section_sub_list">
                                {sections?.map((section) => (
                                    <li key={section.section_id} className="section_sub_list_item">
                                        <div className={`section_link_grp ${showSubSection && section.section_id === currentSection?.section_id && 'active'} ${section?.sub_section_data?.length! > 0 && 'has_child'} `}>
                                            <i className={`bi bi-caret-right section_link_icon ${(section?.sub_section_data?.length === 0 && section?.section_item_data?.length === 0) && 'disabled'}`}></i>
                                            <button className="section_link is_btn"
                                                disabled={section?.sub_section_data?.length === 0 && section?.section_item_data?.length === 0}
                                                onClick={() => handleShowSubSection(section)}>
                                                <span>Section {section.section_no}</span>
                                            </button>
                                        </div>

                                        {section?.sub_section_data?.length! > 0 && (
                                            <ul className="section_sub_list_sub">
                                                {section.sub_section_data?.map((sub_section) => (
                                                    <li className="section_sub_list_item_sub">
                                                        <div className={`section_link_grp ${showSubSectionItem && sub_section.sub_section_id === currentSubSection?.sub_section_id && 'active'} `}>
                                                            <small className={`${(sub_section?.sub_section_item_data?.length === 0) && 'disabled'}`}>{sub_section.sub_section_no}</small>
                                                            <button className="section_link is_btn"
                                                                disabled={sub_section?.sub_section_item_data?.length === 0}
                                                                onClick={() => handleShowSubSectionItem(sub_section)}>
                                                                <span>{sub_section.sub_section_name}</span>
                                                            </button>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </li>

                    {/* Preliminaries */}
                    <li className="section_list_item">
                        <div className={`section_link_grp ${showPreliminaries && 'active'} `}>
                            <i className={`bi bi-caret-right-fill section_link_icon ${(preliminaries?.length === 0) && 'disabled'}`}></i>
                            <button className="section_link is_btn"
                                disabled={preliminaries?.length === 0}
                                onClick={handleShowPreliminaries}>
                                <span>Preliminaries</span>
                            </button>
                        </div>
                    </li>
                </ul>
            </div>

            <div className={`layout_backdrop ${showSideBar ? 'show' : ''}`} onClick={handleShowSideBar}></div>

            <div className={`project_main_content ${showSideBar ? 'has_sidebar' : ''}`}>
                {coverPageBody !== '' && (
                    <article id="coverPage" className="main_section_area d-flex">
                        <div className="cover_page_area">
                            <h2>Schedule of work</h2>
                            <h6>{coverPageBody}</h6>
                            <h5>At</h5>
                            <h5>258-260 LONDON ROAD <br />Croydon CRO 2TH</h5>
                            <br />
                            <h5>For <br />ABC LTD</h5>
                        </div>
                    </article>
                )}

                {sections?.length > 0 && (
                    <article id="content" className="main_section_area">
                        <div className="main_section_header">
                            <h6>Contents</h6>
                        </div>

                        <DataTable
                            value={sections}
                            size="small"
                            tableStyle={{ minWidth: '50rem' }}
                            className="page_table project_table p-0 p-sm-2"
                            rowHover
                            tableClassName="mb-1"
                            rowClassName={getRowClassName}
                            dataKey="section_id"
                            scrollable
                            scrollHeight={'75vh'}
                            emptyMessage="No data found"
                        >
                            <Column
                                header="Section no"
                                alignHeader={'center'}
                                body={(rowData: Section) => rowData.section_no ? <span className="flex_center">{rowData.section_no}</span> : '--'}
                                style={{ width: '15%' }}
                                className="table_column"
                            ></Column>
                            <Column
                                header="Content"
                                body={(rowData: Section) => rowData.section_name ? <span>{rowData.section_name}</span> : '--'}
                                style={{ width: '65%' }}
                                className="table_column"
                            ></Column>
                            <Column
                                header="Price"
                                alignHeader={'right'}
                                body={(rowData: Section) => rowData.section_amount !== null ?
                                    <span className="flex-end text-end text_no_wrap">
                                        {currencySymbol}&nbsp;{formatPrice(rowData.section_amount)}
                                    </span> : '--'}
                                style={{ width: '20%' }}
                                className="table_column text-end"
                            ></Column>
                        </DataTable>
                    </article>
                )}

                {sections.map((section) => (
                    <React.Fragment>
                        {section?.sub_section_data?.length !== 0 && (
                            <React.Fragment>
                                <article id={`section${section?.section_no}`} key={section.section_id} className="main_section_area">
                                    <div className="main_section_header">
                                        <h6>{section?.section_no}. {section?.section_name}</h6>
                                    </div>
                                    <DataTable
                                        value={section?.sub_section_data}
                                        size="small"
                                        tableStyle={{ minWidth: '50rem' }}
                                        className="page_table project_table p-0 p-sm-2"
                                        rowHover
                                        tableClassName="mb-1"
                                        rowClassName={getRowClassName}
                                        dataKey="sub_section_id"
                                        scrollable
                                        scrollHeight={'75vh'}
                                        emptyMessage="No data found"
                                    >
                                        <Column
                                            header="Sub section no."
                                            alignHeader={'center'}
                                            body={(rowData) => rowData.sub_section_no ? <span className="flex_center">{rowData.sub_section_no}</span> : '--'}
                                            style={{ width: '15%' }}
                                            className="table_column"
                                        ></Column>

                                        <Column
                                            header="Sub section name"
                                            body={(rowData) => rowData.sub_section_name ? <span>{rowData.sub_section_name}</span> : '--'}
                                            style={{ width: '65%' }}
                                            className="table_column"
                                        ></Column>

                                        <Column
                                            header="Price"
                                            alignHeader={'right'}
                                            body={(rowData) => rowData.sub_section_amount !== null ?
                                                <span className="flex-end text-end text_no_wrap">
                                                    {currencySymbol}&nbsp;{formatPrice(rowData.sub_section_amount)}
                                                </span> : '--'}
                                            style={{ width: '20%' }}
                                            className="table_column text-end"
                                        ></Column>
                                    </DataTable>
                                </article>

                                {section.sub_section_data?.map((subSection) => {
                                    const filteredSubSectionItemData = subSection.sub_section_item_data?.filter(
                                        (item: SubSectionItem) =>
                                            item.item_no?.toLowerCase().includes(searchKey.toLowerCase()) ||
                                            item.item_name?.toLowerCase().includes(searchKey.toLowerCase())
                                    );

                                    return (
                                        <React.Fragment>
                                            {filteredSubSectionItemData?.length !== 0 && (
                                                <article id={`subsection${subSection?.sub_section_no}`} key={section.section_id} className="main_section_area">
                                                    <div className="main_section_header">
                                                        <h6>Section {section?.section_no} ({subSection?.sub_section_no + ' ' + subSection?.sub_section_name}) </h6>
                                                    </div>

                                                    <DataTable
                                                        value={filteredSubSectionItemData}
                                                        size="small"
                                                        tableStyle={{ minWidth: '50rem' }}
                                                        className="page_table project_table p-0 p-sm-2"
                                                        rowHover
                                                        tableClassName="mb-1"
                                                        rowClassName={getRowClassName}
                                                        dataKey="item_id"
                                                        scrollable
                                                        scrollHeight={'75vh'}
                                                        emptyMessage="No data found"
                                                    >
                                                        <Column
                                                            header="Item no."
                                                            alignHeader={'center'}
                                                            body={(rowData: SubSectionItem) => rowData.item_no ? <span className="flex_center">{rowData.item_no}</span> : '--'}
                                                            style={{ width: '8%' }}
                                                            className="table_column"
                                                        ></Column>

                                                        <Column
                                                            header="Item name"
                                                            body={(rowData: SubSectionItem) => rowData.item_name ? <span>{rowData.item_name}</span> : '--'}
                                                            style={{ width: '30%' }}
                                                            className="table_column"
                                                        ></Column>

                                                        <Column
                                                            header="Unit no."
                                                            alignHeader={'center'}
                                                            style={{ width: '10%' }}
                                                            className="table_column"
                                                        ></Column>

                                                        <Column
                                                            header="Unit price"
                                                            alignHeader={'right'}
                                                            style={{ width: '12%' }}
                                                            className="table_column text-end"
                                                        ></Column>

                                                        <Column
                                                            header="Sub total"
                                                            alignHeader={'right'}
                                                            style={{ width: '12%' }}
                                                            className="table_column text-end"
                                                        ></Column>

                                                        <Column
                                                            header="Note"
                                                            alignHeader={'center'}
                                                            style={{ width: '28%' }}
                                                            className="table_column"
                                                        ></Column>
                                                    </DataTable>
                                                </article>
                                            )}
                                        </React.Fragment>
                                    )
                                })}
                            </React.Fragment>
                        )}

                        {section?.section_item_data?.length !== 0 && (
                            <article id={`section${section?.section_no}`} key={section.section_id} className="main_section_area">
                                <div className="main_section_header">
                                    <h6>{section?.section_no}.&nbsp;{section.section_name}</h6>
                                </div>

                                <DataTable
                                    value={section?.section_item_data}
                                    size="small"
                                    tableStyle={{ minWidth: '50rem' }}
                                    className="page_table project_table p-0 p-sm-2"
                                    rowHover
                                    tableClassName="mb-1"
                                    rowClassName={getRowClassName}
                                    dataKey="item_id"
                                    scrollable
                                    scrollHeight={'75vh'}
                                    emptyMessage="No data found"
                                >
                                    <Column
                                        header="Item no."
                                        alignHeader={'center'}
                                        body={(rowData: SectionItem) => rowData.item_no ? <span className="flex_center">{rowData.item_no}</span> : '--'}
                                        style={{ width: '8%' }}
                                        className="table_column"
                                    ></Column>

                                    <Column
                                        header="Item name"
                                        body={(rowData: SectionItem) => rowData.item_name ? <span>{rowData.item_name}</span> : '--'}
                                        style={{ width: '30%' }}
                                        className="table_column"
                                    ></Column>

                                    <Column
                                        header="Unit no."
                                        alignHeader={'center'}
                                        style={{ width: '10%' }}
                                        className="table_column column_editable"
                                    ></Column>

                                    <Column
                                        header="Unit price"
                                        alignHeader={'right'}
                                        style={{ width: '12%' }}
                                        className="table_column text-end"
                                    ></Column>

                                    <Column
                                        header="Sub total"
                                        alignHeader={'right'}
                                        style={{ width: '12%' }}
                                        className="table_column text-end"
                                    ></Column>

                                    <Column
                                        header="Note"
                                        alignHeader={'center'}
                                        style={{ width: '28%' }}
                                        className="table_column"
                                    ></Column>
                                </DataTable>
                            </article>
                        )}
                    </React.Fragment>
                ))}

                {preliminariesData?.length > 0 && (
                    <article id="preliminary" className="main_section_area">
                        <div className="main_section_header">
                            <h6>Preliminaries</h6>
                        </div>
                        <DataTable
                            value={filteredPreliminariesItemData}
                            size="small"
                            tableStyle={{ minWidth: '50rem' }}
                            className="page_table project_table p-0 p-sm-2"
                            rowHover
                            tableClassName="mb-1"
                            rowClassName={getRowClassName}
                            dataKey="priliminiery_id"
                            scrollable
                            scrollHeight={'75vh'}
                            emptyMessage="No data found"
                        >
                            <Column
                                header="Item no."
                                alignHeader={'center'}
                                body={(rowData) => rowData.ref_number ? <span className="flex_center">{rowData.ref_number}</span> : '--'}
                                style={{ width: '8%' }}
                                className="table_column"
                            ></Column>

                            <Column
                                header="Item name"
                                body={(rowData) => rowData.item_name ? <span>{rowData.item_name}</span> : '--'}
                                style={{ width: '30%' }}
                                className="table_column"
                            ></Column>

                            <Column
                                header="Unit no."
                                alignHeader={'center'}
                                style={{ width: '10%' }}
                                className="table_column text-center column_editable"
                            ></Column>

                            <Column
                                header="Unit price"
                                alignHeader={'right'}
                                style={{ width: '12%' }}
                                className="table_column column_editable text-end"
                            ></Column>

                            <Column
                                header="Sub total"
                                alignHeader={'right'}
                                style={{ width: '12%' }}
                                className="table_column text-end"
                            ></Column>

                            <Column
                                header="Note"
                                alignHeader={'center'}
                                style={{ width: '28%' }}
                                className="table_column"
                            ></Column>
                        </DataTable>
                    </article>
                )}
            </div>

            {/* Download modal */}
            <DownloadSOWTenderList
                isOpen={showDownloadModal}
                onClose={handleHideDownload}
            />

            {/* Search modal */}
            <Dialog headerClassName="custom_modal_header search_modal_header" position="top"
                headerStyle={{ display: 'none' }} dismissableMask
                visible={showSearchModal} className="custom_modal_dialog modal_dialog_sm"
                onHide={() => { if (!showSearchModal) return; setShowSearchModal(false); }}>
                <div className="custom_modal_body">
                    <button className="custom_modal_close float p-ripple"
                        onClick={() => setShowSearchModal(false)}>
                        <i className="bi bi-x-lg"></i>
                        <Ripple />
                    </button>

                    <div className="row">
                        <div className="col-12">
                            <IconField iconPosition="left">
                                <InputIcon className="bi bi-search"> </InputIcon>
                                <InputText
                                    id="searchKey"
                                    name="searchKey"
                                    placeholder="Search by item name and number"
                                    value={searchKey}
                                    autoFocus
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchKey(e.target.value)}
                                />
                            </IconField>
                        </div>
                    </div>
                </div>
            </Dialog>
        </div>
    )
}

export default ViewSOW;